// src/components/Report.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import supabase from '../../supabaseClient';

import FrontCover from './FrontCover';
import ExecutiveSummary from './ExecutiveSummary';
import HighlightsLowlights from './HighlightsLowlights';
import RoomsPages from './RoomsPages';
import TaxDepreciationSchedule from './TaxDepreciationSchedule.js';
// import PriceHistoryPage from './PriceHistoryPage';
import Definitions from './Definitions.js';
import TermsAndConditions from './TermsAndConditions';

const Report = () => {
	const [propertyUUID, setPropertyUUID] = useState(null);
	const [propertyValuation, setPropertyValuation] = useState(null);
	const [pageNumbersObj, setPageNumbersObj] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	
	const location = useLocation();

	// Extract property_uuid from the URL with validation
	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const uuid = queryParams.get('property_uuid');

		if (uuid) {
			setPropertyUUID(uuid);
			console.log('Property UUID:', uuid);
		} else {
			console.warn('Invalid or missing property_uuid in the URL');
			setError('Invalid or missing property_uuid in the URL');
			setLoading(false);
		}
	}, [location.search]);

	
	const setPageNumbers = async (propertyValuation) => {
		const numberOfRooms = propertyValuation.roomClassifications.length
		setPageNumbersObj( {
			'ExecutiveSummary': 2,
			'HighlightsLowlights': 3,
			'TaxDepreciationSchedule': 4,
			'RoomsPages': 5,
			'Definitions': 5 + numberOfRooms*2,
			'TermsAndConditions': 5 + numberOfRooms*2 + 1
		})
	}

	// useEffect hook to fetch data when the component mounts
	useEffect(() => {
		const fetchPropertyValuationData = async () => {
			if (!propertyUUID) return;

			try {
				setLoading(true);
				setError(null);

				// Perform a SELECT query on the property_scans table where property_uuid == propertyUUID
				const { data, error } = await supabase
					.from('property_scans')
					.select('property_valuation') // Adjust fields as necessary
					.eq('property_uuid', propertyUUID)
					.single(); // Use .single() if you expect only one record

				if (error) {
					console.error('Error fetching property_valuation:', error.message);
					setError('Error fetching property valuation data.');
				} else if (!data) {
					console.warn('No data found for the provided propertyUUID.');
					setError('No data found for the provided propertyUUID.');
				} else {
					console.log('Data fetched successfully:', data.property_valuation);
					setPageNumbers(data.property_valuation);
					setPropertyValuation(data.property_valuation);
				}
			} catch (err) {
				console.error('Unexpected error:', err);
				setError('An unexpected error occurred while fetching data.');
			} finally {
				setLoading(false);
			}
		};

		fetchPropertyValuationData();
	}, [propertyUUID]);

	// Example dummy data (if needed for testing

	if (loading) {
		return <div>Loading...</div>;
	}

	if (error) {
		return <div>Error: {error}</div>;
	}

	// Destructure necessary data from propertyValuation
	const propertyDetails = propertyValuation?.propertyAttributes?.propertyDetails;

	if (!propertyDetails) {
		return <div>No property details available.</div>;
	}

	

	const pageNumber=2;

	return (
		<div>
			{/* Front Cover */}
			<FrontCover propertyData={propertyDetails} />

			
			<ExecutiveSummary 
				propertyValuation={propertyValuation}
				pageNumber={pageNumbersObj['ExecutiveSummary']}
			/>

			
			<HighlightsLowlights 
				propertyValuation={propertyValuation}
				pageNumber={pageNumbersObj['HighlightsLowlights']}
			/>

			<TaxDepreciationSchedule 
				pageNumber={pageNumbersObj['TaxDepreciationSchedule']}
				propertyValuation={propertyValuation}
				constructionYear={propertyDetails.propertyYearBuilt} 
			/>
	
			
			<RoomsPages 
				pageNumber={pageNumbersObj['RoomsPages']}
				propertyValuation={propertyValuation}
			/>
	
	
			<Definitions
				pageNumber={pageNumbersObj['Definitions']}
				propertyDetails={propertyDetails} 
			/>
	
			<TermsAndConditions 
				pageNumber={pageNumbersObj['TermsAndConditions']}
				propertyDetails={propertyDetails} 
			/>

		</div>
	);
};

export default Report;
